/**
 * CreatingOrgPage — callback del flujo Google admin (NER-3781 / NER-3785 / NER-3807).
 *
 * Ruta pública `/creating-org`. El backend (adminCallbackGoogle) redirige acá
 * tras el consentimiento de Google con un `#fragment`:
 *   #authToken=<JWT firmado HMAC>          → nuevo usuario → onboarding
 *   #authToken=<...>&existing=1            → ya tiene organización
 *   #authError=google_failed               → error en el OAuth
 *
 * Flujo:
 *  1. Parsear el fragment (y limpiarlo de la URL — el token es sensible).
 *  2. Re-verificar el authToken server-side (POST /auth/verify-signed-token):
 *     el fragment es client-visible y no se confía a ciegas.
 *  3. Mostrar el "confirmation gate" (requisito de seguridad de Diego,
 *     2026-08-11): confirmar explícitamente la creación de la organización
 *     para el email verificado por Google antes de crear nada.
 *  4. Al confirmar, llamar a `POST /social/complete-signup` (NER-3807):
 *     el backend crea el usuario Cognito + la organización y devuelve tokens
 *     Cognito. Persistimos la sesión (persistCognitoSession) y entramos al panel.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  verifySignedToken,
  completeGoogleSignup,
  completeGoogleLogin,
  checkSlugAvailability,
} from '../services/api';
import { persistCognitoSession, adminRootUrl } from '../utils/cognitoSession';
import { storage } from '../utils/storage';
import { trackPixel, trackEvent } from '../utils/analytics';
import AddressAutocomplete from '../components/AddressAutocomplete';
import './AuthPages.css';

// CUIT/CUIL argentino: 11 dígitos. Validación de formato (no de dígito
// verificador — la validación fiscal estricta vive en Configuración Fiscal).
// (NER-4257 follow-up: unificado acá desde el OnboardingWizard, a pedido de
// Diego — se pide junto con el resto de los datos del alta, no en una
// pantalla aparte después del login.)
const isValidCuitFormat = (cuit) => /^\d{11}$/.test(cuit.replace(/\D/g, ''));

// Estados de la máquina de la página.
const STATUS = {
  LOADING: 'loading', // verificando el token
  GATE: 'gate', // confirmation gate visible (nuevo usuario)
  EXISTING: 'existing', // el email ya tiene organización → iniciando sesión (NER-4160)
  SUBMITTING: 'submitting', // creando org + sesión (complete-signup en vuelo)
  ERROR: 'error', // authError o token inválido
};

/** Lee el #fragment una sola vez y lo borra de la URL (token sensible). */
function readAuthFragment() {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.substring(1)
    : window.location.hash;
  const params = new URLSearchParams(hash);
  const result = {
    authToken: params.get('authToken'),
    authError: params.get('authError'),
    existing: params.get('existing') === '1',
  };
  // Limpiar el fragment del historial para no dejar el token en la URL.
  window.history.replaceState(null, '', window.location.pathname + window.location.search);
  return result;
}

function CreatingOrgPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [status, setStatus] = useState(STATUS.LOADING);
  const [profile, setProfile] = useState(null); // { email, given_name, name }
  const [authToken, setAuthToken] = useState(null); // se conserva en memoria para complete-signup
  const [orgName, setOrgName] = useState('');
  // NER-4257 follow-up (roadmap QA 2026-09-10 #4): el flujo Google creaba la
  // organizacion pero nunca pedia el restaurante, a diferencia del wizard de
  // registro por email (Step 2) -> el usuario terminaba aterrizando derecho
  // en /restaurant/new sin haber pasado por ningun formulario. Se agregan
  // estos dos campos al mismo gate para pedir la info minima acá también.
  const [restaurantName, setRestaurantName] = useState('');
  const [address, setAddress] = useState('');
  const [restaurantCoords, setRestaurantCoords] = useState(null);
  const [cuit, setCuit] = useState('');
  const [cuitError, setCuitError] = useState('');
  const [error, setError] = useState('');
  // Hallazgo QA 2026-09-10 (ronda 3): chequeo de disponibilidad del nombre
  // ANTES de crear el restaurante, para no repetir el 409 silencioso que
  // dejaba al usuario en /restaurant/new sin ninguna explicación. 'idle' |
  // 'checking' | 'available' | 'taken'.
  const [nameAvailability, setNameAvailability] = useState('idle');
  const nameCheckTimer = useRef(null);
  const nameCheckSeq = useRef(0);

  const handleCuitChange = (e) => {
    let val = e.target.value.replace(/\D/g, '').slice(0, 11);
    if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
    if (val.length > 10) val = val.slice(0, 11) + '-' + val.slice(11);
    setCuit(val);
    setCuitError('');
  };

  // Mismo algoritmo que RegisterPage.generateSlug — se duplica localmente
  // (no hay un util compartido todavía) para no acoplar ambas páginas.
  const generateSlug = useCallback((name) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50);
  }, []);

  // Hallazgo QA 2026-09-10 (ronda 3): mientras el usuario tipea el nombre del
  // restaurante, chequeamos en segundo plano (debounced, endpoint público
  // /restaurants/public/resolve-slug-global) si el slug ya está en uso por
  // OTRO restaurante activo — así el gate avisa "nombre ya usado" antes de
  // confirmar, en vez de que la creación best-effort post-signup falle en
  // silencio con un 409 y el usuario termine en /restaurant/new sin motivo.
  useEffect(() => {
    const trimmed = restaurantName.trim();
    if (nameCheckTimer.current) clearTimeout(nameCheckTimer.current);

    if (!trimmed) {
      setNameAvailability('idle');
      return undefined;
    }

    const slug = generateSlug(trimmed);
    if (!slug) {
      setNameAvailability('idle');
      return undefined;
    }

    setNameAvailability('checking');
    const seq = ++nameCheckSeq.current;
    nameCheckTimer.current = setTimeout(async () => {
      try {
        const available = await checkSlugAvailability(slug);
        if (seq !== nameCheckSeq.current) return; // respuesta obsoleta, el usuario siguió tipeando
        setNameAvailability(available ? 'available' : 'taken');
        if (!available) setError(t('register.errors.restaurantNameTaken'));
      } catch (checkErr) {
        // Si el chequeo en sí falla (red/5xx), no bloqueamos al usuario acá —
        // el gate de handleConfirm hace su propio chequeo antes de enviar.
        if (seq === nameCheckSeq.current) setNameAvailability('idle');
      }
    }, 500);

    return () => clearTimeout(nameCheckTimer.current);
  }, [restaurantName, generateSlug, t]);

  useEffect(() => {
    let cancelled = false;
    const { authToken: token, authError, existing } = readAuthFragment();

    if (authError || !token) {
      setError(t('creatingOrg.errors.googleFailed'));
      setStatus(STATUS.ERROR);
      return;
    }

    setAuthToken(token);

    (async () => {
      try {
        const claims = await verifySignedToken(token);
        if (cancelled) return;
        if (claims.flow !== 'admin') {
          // Token de otro flujo (p. ej. customer) no debe onboardear el panel.
          setError(t('creatingOrg.errors.invalidToken'));
          setStatus(STATUS.ERROR);
          return;
        }
        setProfile(claims);
        // Prefill del nombre de organización con el nombre de la persona.
        setOrgName(claims.given_name || claims.name || '');

        if (!existing) {
          // Usuario nuevo → gate de confirmación + creación de organización.
          setStatus(STATUS.GATE);
          return;
        }

        // ── NER-4160: usuario YA EXISTENTE → login directo, sin gate ─────────
        // El email ya tiene organización. Este usuario se registró con Google y
        // no conoce su contraseña (aleatoria), así que NO puede usar el form
        // email/password: quedaba lockeado en la pantalla "ya tenés cuenta".
        // Ahora re-emitimos una sesión Cognito server-side (complete-login) y
        // entramos directo al panel — el comportamiento esperado de un login OAuth.
        setStatus(STATUS.EXISTING); // pantalla "iniciando sesión…"
        try {
          const { status: httpStatus, data } = await completeGoogleLogin(token);
          if (cancelled) return;

          if (httpStatus === 201 && data && data.tokens) {
            // Sesión Cognito lista → persistir tokens + entrar al panel.
            await persistCognitoSession(data.tokens);
            try {
              trackEvent('login', { login_method: 'google' });
            } catch (evtErr) {
              // Analytics nunca bloquea el login.
              console.warn('Evento login (google) no enviado:', evtErr);
            }
            // Hard redirect: fuerza a Amplify a levantar la sesión desde cookies.
            window.location.assign(adminRootUrl());
            return;
          }

          if (httpStatus === 201) {
            // 201 parcial (tokens=null): la re-emisión de sesión falló pero el
            // usuario existe → mandarlo al login manual con su email prefilleado.
            const email = claims.email || '';
            navigate(
              `/login?warning=session_pending${email ? `&email=${encodeURIComponent(email)}` : ''}`
            );
            return;
          }

          if (httpStatus === 404) {
            // El email no tiene usuario Cognito (no debería con existing=1) → registro.
            navigate('/register?flow=google');
            return;
          }

          if (httpStatus === 401) {
            // authToken inválido/expirado/no-admin → reiniciar desde login.
            setError(t('creatingOrg.errors.invalidToken'));
            return;
          }

          // Otro 4xx → mostrar fallback con acceso al login (que tiene botón Google).
          setError(t('creatingOrg.errors.loginFailed'));
        } catch (loginErr) {
          if (cancelled) return;
          // 5xx / red → fallback al login (reintentable con el botón de Google).
          console.error('complete-login falló:', loginErr);
          setError(t('creatingOrg.errors.loginFailed'));
        }
      } catch (err) {
        if (cancelled) return;
        setError(t('creatingOrg.errors.invalidToken'));
        setStatus(STATUS.ERROR);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [t, navigate]);

  const handleConfirm = useCallback(
    async (e) => {
      e.preventDefault();
      setError('');
      const name = orgName.trim();
      const restaurantNameTrimmed = restaurantName.trim();
      const addressTrimmed = address.trim();
      if (!name) {
        setError(t('creatingOrg.errors.orgNameRequired'));
        return;
      }
      if (!restaurantNameTrimmed) {
        setError(t('register.errors.restaurantNameRequired'));
        return;
      }
      if (!addressTrimmed) {
        setError(t('register.errors.restaurantAddressRequired'));
        return;
      }
      const cuitTrimmed = cuit.trim();
      if (cuitTrimmed && !isValidCuitFormat(cuitTrimmed)) {
        setCuitError(t('register.errors.cuitInvalid'));
        return;
      }
      if (!authToken) {
        setError(t('creatingOrg.errors.invalidToken'));
        setStatus(STATUS.ERROR);
        return;
      }

      // Hallazgo QA 2026-09-10 (ronda 3): re-chequeo sincrónico justo antes de
      // enviar (no confiar solo en el chequeo debounced de fondo, que puede no
      // haber terminado si el usuario tipeó y confirmó muy rápido).
      const restaurantSlugToCheck = generateSlug(restaurantNameTrimmed);
      if (nameAvailability === 'taken') {
        setError(t('register.errors.restaurantNameTaken'));
        return;
      }
      try {
        const available = await checkSlugAvailability(restaurantSlugToCheck);
        if (!available) {
          setNameAvailability('taken');
          setError(t('register.errors.restaurantNameTaken'));
          return;
        }
      } catch (availabilityErr) {
        // Si el chequeo mismo falla (red/5xx), no bloqueamos el signup —
        // createRestaurant server-side vuelve a validar unicidad igual.
        console.warn('[CreatingOrgPage] No se pudo verificar disponibilidad del nombre:', availabilityErr.message);
      }

      // NER-3807: crear org + usuario Cognito + sesión vía endpoint público.
      setStatus(STATUS.SUBMITTING);
      try {
        const { status: httpStatus, data } = await completeGoogleSignup(
          authToken,
          name,
          cuitTrimmed.replace(/\D/g, '')
        );

        if (httpStatus === 201 && data && data.organization) {
          // NER-4257 follow-up #4: la organización ya se creó synchronously acá
          // (a diferencia del flujo email, que la crea recién al confirmar el
          // código y recuperarla en el próximo checkUser). El restaurante NO
          // se puede crear todavía — todavía no hay sesión Cognito utilizable
          // en esta pestaña (persistCognitoSession sólo escribe cookies; Amplify
          // recién la levanta tras el hard-redirect). Se deja en localStorage
          // para que AuthContext.checkUser() la cree apenas la sesión exista,
          // sea vía el hard-redirect de abajo o vía el login manual del caso
          // "201 parcial" (session_pending) más abajo.
          storage.set(
            'pendingRestaurantData',
            JSON.stringify({
              restaurantName: restaurantNameTrimmed,
              restaurantSlug: generateSlug(restaurantNameTrimmed),
              address: addressTrimmed,
              ...(restaurantCoords
                ? {
                    location: {
                      lat: String(restaurantCoords.lat),
                      lng: String(restaurantCoords.lng),
                    },
                  }
                : {}),
            })
          );
        }

        if (httpStatus === 201 && data && data.tokens) {
          // Éxito: sesión Cognito lista → persistir tokens + pixel + entrar al panel.
          await persistCognitoSession(data.tokens);
          try {
            trackPixel('CompleteRegistration', {
              content_name: 'resto_admin_register_google',
            });
          } catch (pixelErr) {
            // Analytics nunca bloquea el alta.
            console.warn('Pixel CompleteRegistration (google) no enviado:', pixelErr);
          }
          // Hard redirect: fuerza a Amplify a levantar la sesión desde las cookies.
          window.location.assign(adminRootUrl());
          return;
        }

        if (httpStatus === 201) {
          // 201 parcial: org creada pero tokens=null → completar login manual.
          const email = (data && data.organization && data.organization.email) || (profile && profile.email) || '';
          navigate(
            `/login?warning=session_pending${email ? `&email=${encodeURIComponent(email)}` : ''}`
          );
          return;
        }

        if (httpStatus === 409) {
          // Usuario ya existe → rutear a login/registro.
          navigate('/register?error=user_already_exists&flow=google');
          return;
        }

        if (httpStatus === 401) {
          // authToken inválido/expirado/no-admin → reiniciar registro.
          navigate('/register?error=token_invalid');
          return;
        }

        // 400 (body/organizationName inválido) u otro 4xx → reintentar en el gate.
        setError(t('creatingOrg.errors.completeFailed'));
        setStatus(STATUS.GATE);
      } catch (err) {
        // 5xx / error de red → error genérico, permitir reintento.
        console.error('complete-signup falló:', err);
        setError(t('creatingOrg.errors.completeFailed'));
        setStatus(STATUS.GATE);
      }
    },
    [
      authToken,
      orgName,
      restaurantName,
      address,
      restaurantCoords,
      cuit,
      nameAvailability,
      generateSlug,
      profile,
      navigate,
      t,
    ]
  );

  const goToEmailSignup = () => navigate('/register');
  const goToLogin = () => navigate('/login');

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Resto</h1>
          <h2>
            {status === STATUS.LOADING && t('creatingOrg.verifyingTitle')}
            {status === STATUS.GATE && t('creatingOrg.gateTitle')}
            {status === STATUS.EXISTING && t('creatingOrg.existingTitle')}
            {status === STATUS.SUBMITTING && t('creatingOrg.settingUpTitle')}
            {status === STATUS.ERROR && t('creatingOrg.errorTitle')}
          </h2>
        </div>

        {status === STATUS.LOADING && (
          <div className="auth-form">
            <div className="loading-message">
              <p>{t('creatingOrg.verifyingMessage')}</p>
            </div>
          </div>
        )}

        {status === STATUS.SUBMITTING && (
          <div className="auth-form">
            <div className="loading-message">
              <p>{t('creatingOrg.creatingMessage')}</p>
            </div>
          </div>
        )}

        {status === STATUS.GATE && profile && (
          <form className="auth-form" onSubmit={handleConfirm} noValidate>
            <p className="confirmation-message">
              {t('creatingOrg.gateIntro')} <strong>{profile.email}</strong>.
            </p>

            <div className="form-group">
              <label htmlFor="orgName">{t('creatingOrg.orgNameLabel')} *</label>
              <input
                type="text"
                id="orgName"
                name="orgName"
                value={orgName}
                onChange={(e) => {
                  setOrgName(e.target.value);
                  setError('');
                }}
                placeholder={t('creatingOrg.orgNamePlaceholder')}
                autoFocus
              />
            </div>

            <div className="form-group">
              <label htmlFor="restaurantName">{t('register.restaurantName')} *</label>
              <input
                type="text"
                id="restaurantName"
                name="restaurantName"
                value={restaurantName}
                onChange={(e) => {
                  setRestaurantName(e.target.value);
                  if (error === t('register.errors.restaurantNameTaken')) setError('');
                }}
                placeholder={t('register.restaurantNamePlaceholder')}
              />
              {nameAvailability === 'checking' && (
                <small className="form-help">{t('register.checkingAvailability')}</small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="address">{t('register.address')} *</label>
              <AddressAutocomplete
                id="address"
                name="address"
                value={address}
                placeholder={t('register.addressPlaceholder')}
                onChange={(newAddress) => {
                  setAddress(newAddress);
                  setError('');
                }}
                onPlaceSelected={({ address: placeAddress, coords }) => {
                  setAddress(placeAddress || address);
                  setRestaurantCoords(coords || null);
                  setError('');
                }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="cuit">{t('register.cuitLabel')}</label>
              <input
                type="text"
                id="cuit"
                name="cuit"
                inputMode="numeric"
                value={cuit}
                placeholder="20-12345678-9"
                onChange={handleCuitChange}
              />
              {cuitError && <div className="error-message">{cuitError}</div>}
              <small className="form-help">{t('register.cuitHint')}</small>
            </div>

            {error && (
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={goToEmailSignup}
                style={{ flex: 1 }}
              >
                {t('creatingOrg.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" style={{ flex: 2 }}>
                {t('creatingOrg.confirm')}
              </button>
            </div>
          </form>
        )}

        {status === STATUS.EXISTING &&
          (error ? (
            // NER-4160: la re-emisión de sesión falló → fallback al login, que
            // ahora tiene el botón de Google para reintentar el ingreso.
            <div className="auth-form">
              <div className="error-message" role="alert" aria-live="polite">
                {error}
              </div>
              <button type="button" className="btn btn-primary btn-block" onClick={goToLogin}>
                {t('creatingOrg.goToLogin')}
              </button>
            </div>
          ) : (
            // NER-4160: usuario existente → iniciando sesión automáticamente.
            <div className="auth-form">
              <div className="loading-message">
                <p>{t('creatingOrg.signingInMessage')}</p>
              </div>
            </div>
          ))}

        {status === STATUS.ERROR && (
          <div className="auth-form">
            <div className="error-message" role="alert" aria-live="polite">
              {error}
            </div>
            <button type="button" className="btn btn-primary btn-block" onClick={goToEmailSignup}>
              {t('creatingOrg.backToRegister')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreatingOrgPage;
